import { useEffect, useState } from "react";
import { CheckCircle2, FlaskConical, PauseCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

/* ---------- Layout primitives (re-exported from the canonical design system) ---------- */

export { Card, CardHeader, Field, SwitchRow } from "@/components/design-system";

/* ---------- Controls ---------- */

export const Segmented = ({ options, value, onChange, testId }) => (
    <div className="flex w-full rounded-lg border border-border bg-muted/40 p-0.5" data-testid={testId}>
        {options.map((o) => (
            <button
                key={o.value}
                type="button"
                onClick={() => onChange(o.value)}
                data-testid={testId ? `${testId}-${o.value}` : undefined}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 h-8 rounded-md text-xs font-medium transition-colors ${
                    value === o.value
                        ? "bg-surface border border-border shadow-sm text-foreground"
                        : "border border-transparent text-muted-foreground hover:text-foreground"
                }`}
            >
                {o.icon && <o.icon className="h-3.5 w-3.5" strokeWidth={1.75} />}
                {o.label}
            </button>
        ))}
    </div>
);

export const IconPicker = ({ icons, value, onChange, testId }) => (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
        {icons.map((ic) => (
            <button
                key={ic.key}
                type="button"
                onClick={() => onChange(ic.key)}
                title={ic.label}
                data-testid={testId ? `${testId}-${ic.key}` : undefined}
                className={`h-9 min-w-9 px-1.5 grid place-items-center rounded-lg border transition-all ${
                    value === ic.key
                        ? "border-foreground bg-foreground text-background"
                        : "border-border bg-background text-muted-foreground hover:text-foreground hover:border-foreground/40"
                }`}
            >
                {ic.icon ? <ic.icon className="h-4 w-4" strokeWidth={1.75} /> : <span className="text-[10px] font-medium px-1">Yok</span>}
            </button>
        ))}
    </div>
);

export const COLOR_SWATCHES = ["#18181B", "#2563EB", "#16A34A", "#DC2626", "#7C3AED", "#EA580C"];

export const ColorPicker = ({ value, onChange, testId }) => (
    <div className="flex items-center gap-2 flex-wrap" data-testid={testId}>
        <div className="flex gap-1.5">
            {COLOR_SWATCHES.map((c) => (
                <button
                    key={c}
                    type="button"
                    onClick={() => onChange(c)}
                    data-testid={testId ? `${testId}-swatch-${c.replace("#", "")}` : undefined}
                    className={`h-7 w-7 rounded-full transition-transform hover:scale-110 ${
                        value.toLowerCase() === c.toLowerCase() ? "ring-2 ring-offset-2 ring-foreground ring-offset-surface" : ""
                    }`}
                    style={{ backgroundColor: c }}
                />
            ))}
        </div>
        <div className="flex items-center gap-1.5 ml-auto">
            <span className="h-7 w-7 rounded-md border border-border shrink-0" style={{ backgroundColor: value }} />
            <Input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="h-7 w-[86px] text-[11px] font-mono px-2"
                data-testid={testId ? `${testId}-hex` : undefined}
            />
        </div>
    </div>
);

/* ---------- Preview primitives ---------- */

export const BrowserFrame = ({ url, children }) => (
    <div className="rounded-lg overflow-hidden bg-surface border border-border">
        <div className="px-3 py-2 border-b border-border flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
            <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
            <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
            <span className="ml-2 text-[10px] font-mono text-muted-foreground truncate">{url}</span>
        </div>
        {children}
    </div>
);

const SIZE_CLS = {
    sm: "h-8 px-3 text-[11px]",
    md: "h-9 px-4 text-xs",
    lg: "h-11 px-5 text-sm",
};
const RADIUS_CLS = { none: "rounded-none", md: "rounded-md", full: "rounded-full" };

export const TriggerButton = ({ cfg, iconMap, testId }) => {
    const Icon = iconMap[cfg.icon];
    if (cfg.variant === "link") {
        return (
            <button
                type="button"
                data-testid={testId}
                className="inline-flex items-center gap-1.5 text-xs font-medium underline underline-offset-4"
                style={{ color: cfg.color }}
            >
                {Icon && <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />}
                {cfg.text}
            </button>
        );
    }
    const solid = cfg.variant === "solid";
    return (
        <button
            type="button"
            data-testid={testId}
            className={`inline-flex items-center justify-center gap-1.5 font-medium transition-transform hover:scale-[1.02] ${SIZE_CLS[cfg.size]} ${RADIUS_CLS[cfg.radius]}`}
            style={
                solid
                    ? { backgroundColor: cfg.color, color: "#FFFFFF" }
                    : { border: `1.5px solid ${cfg.color}`, color: cfg.color, backgroundColor: "transparent" }
            }
        >
            {Icon && <Icon className={cfg.size === "lg" ? "h-4 w-4" : "h-3.5 w-3.5"} strokeWidth={1.75} />}
            {cfg.text}
        </button>
    );
};

/* ---------- Widget mode (Active / Test / Passive) ---------- */

export const MODE_OPTIONS = [
    { value: "active", label: "Aktif", icon: CheckCircle2 },
    { value: "test", label: "Test", icon: FlaskConical },
    { value: "passive", label: "Pasif", icon: PauseCircle },
];

export const ModeSegmented = ({ value, onChange, testId, disabled = false }) => (
    <div
        className={`inline-flex rounded-lg border border-border bg-muted/40 p-0.5 h-9 ${disabled ? "opacity-60 pointer-events-none" : ""}`}
        data-testid={testId}
    >
        {MODE_OPTIONS.map((o) => {
            const active = value === o.value;
            const activeCls =
                o.value === "active"
                    ? "bg-success-soft border-success/20 text-success"
                    : o.value === "test"
                        ? "bg-info-soft border-info/20 text-info"
                        : "bg-destructive-soft border-destructive/20 text-destructive";
            return (
                <button
                    key={o.value}
                    type="button"
                    onClick={() => onChange(o.value)}
                    data-testid={testId ? `${testId}-${o.value}` : undefined}
                    className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium border transition-colors ${
                        active ? activeCls : "border-transparent text-muted-foreground hover:text-foreground"
                    }`}
                >
                    <o.icon className="h-3.5 w-3.5" strokeWidth={1.75} />
                    {o.label}
                </button>
            );
        })}
    </div>
);

export const ModeStatusPill = ({ mode, testId }) => {
    const cfg = mode === "active"
        ? { label: "Aktif", cls: "bg-success-soft text-success border-success/20", dot: "bg-success animate-pulse-dot" }
        : mode === "test"
            ? { label: "Test", cls: "bg-info-soft text-info border-info/20", dot: "bg-info animate-pulse-dot" }
            : { label: "Pasif", cls: "bg-destructive-soft text-destructive border-destructive/20", dot: "bg-destructive" };
    return (
        <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 h-6 text-[11px] font-medium leading-none align-middle ${cfg.cls}`}
            data-testid={testId}
        >
            <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />
            {cfg.label}
        </span>
    );
};

/* ---------- Premium design-token controls ---------- */

export const PREMIUM_ACCENTS = [
    { value: "#2B2A27", name: "Charcoal" },
    { value: "#4A3B2E", name: "Espresso" },
    { value: "#B4552D", name: "Terracotta" },
    { value: "#6F7D5F", name: "Sage" },
    { value: "#B07D75", name: "Dusty Rose" },
    { value: "#1E4B5F", name: "Ocean" },
    { value: "#9C6B3C", name: "Cognac" },
    { value: "#A8925F", name: "Champagne" },
];

export const PremiumAccentPicker = ({ value, onChange, testId }) => {
    const isPreset = PREMIUM_ACCENTS.some((s) => s.value.toLowerCase() === (value || "").toLowerCase());
    return (
        <div className="space-y-3" data-testid={testId}>
            <div className="grid grid-cols-8 gap-1.5">
                {PREMIUM_ACCENTS.map((s) => {
                    const active = (value || "").toLowerCase() === s.value.toLowerCase();
                    return (
                        <button
                            key={s.value}
                            type="button"
                            title={s.name}
                            onClick={() => onChange(s.value)}
                            data-testid={`${testId}-${s.name.toLowerCase().replace(/\s/g, "-")}`}
                            className="group flex flex-col items-center gap-1 min-w-0"
                            aria-pressed={active}
                        >
                            <span
                                className={`h-8 w-full rounded-md transition-all ${active ? "ring-2 ring-offset-2 ring-foreground ring-offset-surface" : "group-hover:scale-[1.06]"}`}
                                style={{ backgroundColor: s.value }}
                            />
                            <span className={`text-[8.5px] leading-none truncate w-full text-center ${active ? "text-foreground font-medium" : "text-muted-foreground"}`}>
                                {s.name}
                            </span>
                        </button>
                    );
                })}
            </div>

            <div className="flex items-center gap-2.5">
                <div className="flex-1 h-px bg-border/60" />
                <span className="text-[9.5px] font-medium tracking-wider uppercase text-muted-foreground">
                    {isPreset ? "veya kendi rengin" : "özel renk aktif"}
                </span>
                <div className="flex-1 h-px bg-border/60" />
            </div>

            <CustomAccentInput value={value} onChange={onChange} testId={testId} />
        </div>
    );
};

/* ---------- CustomAccentInput ----------
 * Minimal, hex-only accent color input:
 *  - Compact 28×28 swatch (read-only visual — no click-to-open picker)
 *  - # prefixed hex input with local buffered typing (commit on blur/Enter)
 *  - Live validation: valid full hex commits immediately; invalid renders in destructive text
 *  - Focus-within border highlight; auto-normalizes to 6-digit uppercase; supports #RGB shorthand
 */
const HEX_RE = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const normalizeHex = (raw) => {
    let v = String(raw || "").trim().replace(/^#/, "");
    if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(v)) return null;
    if (v.length === 3) v = v.split("").map((c) => c + c).join("");
    return "#" + v.toUpperCase();
};

export const CustomAccentInput = ({ value, onChange, testId }) => {
    const [buffer, setBuffer] = useState(String(value || "").replace(/^#/, "").toUpperCase());
    const [invalid, setInvalid] = useState(false);

    // Keep buffer in sync when parent value changes (e.g. preset click)
    useEffect(() => {
        setBuffer(String(value || "").replace(/^#/, "").toUpperCase());
        setInvalid(false);
    }, [value]);

    const commit = (raw) => {
        const next = normalizeHex(raw);
        if (next) {
            onChange(next);
            setBuffer(next.replace(/^#/, ""));
            setInvalid(false);
        } else {
            setInvalid(true);
        }
    };

    return (
        <div
            className={`flex items-center gap-2 rounded-lg border bg-background pl-1.5 pr-2.5 h-9 transition-colors ${invalid ? "border-destructive/60" : "border-border/70 focus-within:border-foreground/40"}`}
            data-testid={`${testId}-custom`}
        >
            {/* Swatch — pure visual, reflects current accent */}
            <span
                className="h-6 w-6 shrink-0 rounded-md border border-border/70"
                style={{ backgroundColor: value || "#2B2A27" }}
                data-testid={`${testId}-swatch`}
                aria-hidden
            />

            {/* Hex input with # prefix */}
            <div className="flex items-baseline flex-1 min-w-0 gap-0.5">
                <span className={`text-[12px] font-mono select-none ${invalid ? "text-destructive" : "text-muted-foreground"}`}>#</span>
                <input
                    value={buffer}
                    onChange={(e) => {
                        const cleaned = e.target.value.replace(/[^0-9a-fA-F]/g, "").slice(0, 6).toUpperCase();
                        setBuffer(cleaned);
                        if (cleaned.length === 6 && HEX_RE.test("#" + cleaned)) {
                            onChange("#" + cleaned);
                            setInvalid(false);
                        } else if (cleaned.length === 6) {
                            setInvalid(true);
                        } else {
                            setInvalid(false);
                        }
                    }}
                    onBlur={(e) => commit(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            commit(e.currentTarget.value);
                            e.currentTarget.blur();
                        }
                    }}
                    maxLength={6}
                    spellCheck={false}
                    inputMode="text"
                    className={`flex-1 min-w-0 bg-transparent border-0 outline-none text-[12px] font-mono tracking-[0.06em] placeholder:text-muted-foreground/40 ${invalid ? "text-destructive" : "text-foreground"}`}
                    placeholder="2B2A27"
                    data-testid={`${testId}-hex`}
                    aria-label="Hex renk kodu"
                    aria-invalid={invalid}
                />
            </div>

            <span className="text-[9.5px] font-semibold tracking-wider uppercase text-muted-foreground select-none">
                Hex
            </span>
        </div>
    );
};

export const GradientPicker = ({ options, value, onChange, testId }) => (
    <div className="grid grid-cols-3 gap-2" data-testid={testId}>
        {options.map((o) => {
            const active = value === o.value;
            return (
                <button
                    key={o.value}
                    type="button"
                    onClick={() => onChange(o.value)}
                    data-testid={`${testId}-${o.value}`}
                    className={`rounded-lg border p-3 text-left transition-all ${active ? "border-foreground bg-muted/40 shadow-sm" : "border-border bg-background hover:border-foreground/30"}`}
                >
                    <span className="block h-1.5 w-full rounded-full" style={{ background: o.preview }} />
                    <span className="block text-[11px] font-semibold mt-2">{o.label}</span>
                    <span className="block text-[10px] text-muted-foreground mt-0.5 leading-snug">{o.desc}</span>
                </button>
            );
        })}
    </div>
);

/* ---------- Theme-adaptive color engine ---------- */
/* Guarantees flawless contrast on both themes for ANY hex the merchant picks:
   dark → lift lightness into a pastel band (0.62–0.78) + desaturate ~25%,
   light → pull overly light colors down into a readable band. */

export function hexToHsl(hex) {
    let x = String(hex || "").trim().replace("#", "");
    if (x.length === 3) x = x.split("").map((c) => c + c).join("");
    if (!/^[0-9a-fA-F]{6}$/.test(x)) return null;
    const r = parseInt(x.slice(0, 2), 16) / 255, g = parseInt(x.slice(2, 4), 16) / 255, b = parseInt(x.slice(4, 6), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b), l = (max + min) / 2;
    let h = 0, s = 0;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
        else if (max === g) h = (b - r) / d + 2;
        else h = (r - g) / d + 4;
        h /= 6;
    }
    return { h, s, l };
}

export function hslToHex(h, s, l) {
    const f = (n) => {
        const k = (n + h * 12) % 12;
        const a = s * Math.min(l, 1 - l);
        const c = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
        return Math.round(c * 255).toString(16).padStart(2, "0");
    };
    return `#${f(0)}${f(8)}${f(4)}`;
}

export function adaptColor(hex, dark) {
    const c = hexToHsl(hex);
    if (!c) return hex;
    let { h, s, l } = c;
    if (dark) { l = 0.62 + l * 0.16; s = Math.min(s * 0.75, 0.55); }
    else if (l > 0.68) { l = 0.45; s = Math.min(s, 0.65); }
    return hslToHex(h, s, l);
}

export function pastelColor(hex, dark) {
    const c = hexToHsl(hex);
    if (!c) return hex;
    return hslToHex(c.h, Math.min(c.s * 0.55, 0.45), dark ? 0.56 : 0.78);
}

export function barGradient(scheme, accent, dark) {
    if (scheme === "tonal") return `linear-gradient(90deg,${pastelColor(accent, dark)},${adaptColor(accent, dark)})`;
    if (scheme === "neutral") return dark ? "linear-gradient(90deg,#4A4843,#9B978F)" : "linear-gradient(90deg,#DAD6CE,#8A8680)";
    return `linear-gradient(90deg,${pastelColor("#B4552D", dark)} 0%,${pastelColor("#C9A227", dark)} 50%,${pastelColor("#4C7A5C", dark)} 100%)`;
}

/* ---------- Config page skeleton (passive→active flash fix) ---------- */

export const WidgetConfigSkeleton = ({ testId = "widget-config-skeleton" }) => (
    <div className="space-y-6" data-testid={testId}>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div className="space-y-2.5">
                <div className="flex items-center gap-2.5">
                    <Skeleton className="h-9 w-9 rounded-lg" />
                    <Skeleton className="h-7 w-44" />
                    <Skeleton className="h-5 w-16 rounded-full" />
                </div>
                <Skeleton className="h-3.5 w-72" />
            </div>
            <div className="flex items-center gap-3 shrink-0">
                <Skeleton className="h-9 w-56 rounded-lg" />
                <Skeleton className="h-9 w-28 rounded-lg" />
            </div>
        </div>
        <div className="flex gap-5 border-b border-border pb-3">
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-4 w-16" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-7">
                <div className="rounded-xl border border-border bg-surface p-5 space-y-5">
                    <Skeleton className="h-4 w-36" />
                    <div className="grid grid-cols-3 gap-3">
                        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
                    </div>
                    <Skeleton className="h-9 w-full rounded-lg" />
                    <Skeleton className="h-9 w-2/3 rounded-lg" />
                </div>
            </div>
            <div className="lg:col-span-5 space-y-2">
                <Skeleton className="h-3.5 w-20" />
                <Skeleton className="h-72 w-full rounded-xl" />
            </div>
        </div>
    </div>
);

/**
 * Contextual banner shown under the header:
 * - active:  hidden
 * - test:    info banner explaining ?sayz_test=1 gating
 * - passive: warning banner explaining widget is off
 */
export const ModeBanner = ({ mode, activeCopy, testCopy, passiveCopy, testId }) => {
    if (mode === "active") return null;
    if (mode === "test") {
        return (
            <div
                className="flex items-center gap-2.5 rounded-lg border border-info/25 bg-info-soft px-4 py-2.5 text-xs text-info"
                data-testid={testId}
            >
                <FlaskConical className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
                <span>
                    Test modu — {testCopy || "widget yalnızca URL'ye "}
                    <code className="font-mono bg-info/10 px-1 py-0.5 rounded text-[11px]">?sayz_test=1</code>
                    {testCopy ? "" : " parametresi eklendiğinde görünür. Müşterileriniz göremez."}
                </span>
            </div>
        );
    }
    return (
        <div
            className="flex items-center gap-2.5 rounded-lg border border-destructive/25 bg-destructive-soft px-4 py-2.5 text-xs text-destructive"
            data-testid={testId}
        >
            <PauseCircle className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
            {passiveCopy || activeCopy || "Widget şu anda pasif — ürün sayfalarında gösterilmiyor."}
        </div>
    );
};
